import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Grid,
  Avatar,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { fetchUserNameById } from '../DatabaseApis/DBQuery';

const RiskCardDetailsDialog = ({ open, onClose, card, onEdit }) => {
  // Simple check: if card status is "Done", disable editing
  const isDone = card?.cardStatus === 'Done';
  
  // State variables to hold the usernames of the user who created and updated the card
  const [createdByUserName, setCreatedByUserName] = useState(null);
  const [updatedByUserName, setUpdatedByUserName] = useState(null);

  useEffect(() => {
    const fetchUserNames = async () => {
      if (card) {
        const createdBy = await fetchUserNameById(card.cardCreatedByUserId);
        const updatedBy = await fetchUserNameById(card.cardUpdatedByUserId);
        
        setCreatedByUserName(createdBy);
        setUpdatedByUserName(updatedBy);
      }
    };
  
    fetchUserNames();
  }, [card]);

  if (!card) return null;
  const template = card.templateData || {};
  const riskScore = template.riskScore || 1;
  const assignee = card.assigneeName ||template.actionOwner || 'N/A';

  /**
   * Formats timestamp to a consistent format: DD/MMM/YYYY, HH:MM:SS AM/PM
   */
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown Date';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'Unknown Date';
    const formattedDate = date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
    // Split the date and time parts
    const [datePart, timePart] = formattedDate.split(', ');
    return `${datePart.replace(/ /g, "/")}, ${timePart || ''}`;
  };

  const localCreationDate = card.cardCreationDateDisplay
    ? formatTimestamp(card.cardCreationDateDisplay)
    : 'Unknown Date';

  const localUpdatedDate = card.cardUpdatedOn
    ? formatTimestamp(card.cardUpdatedOn)
    : 'Unknown Date';

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        // Only allow closing via explicit close button, not backdrop click or escape key
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }} 
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown
    >
      <Box sx={{ background: 'linear-gradient(45deg, #1e88e5 30%, #66bb6a 90%)', color: '#fff', fontWeight: 'bold', textAlign: 'center', position: 'relative' }}>
        <DialogTitle sx={{ color: 'white' }}>
          Card Details
          
          {/* Edit Button - hidden if card is in "Done" column */}
          {onEdit && !isDone && (
            <IconButton
              onClick={() => onEdit(card)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8, 
                color: 'white',
              }}
            >
              <EditIcon />
            </IconButton>
          )}

          {/* Close Button */}
          <IconButton onClick={onClose} sx={{ position: 'absolute', left: 8, top: 8, color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
      </Box>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6">{template.summary}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1"><strong>Card ID:</strong> {card.cardId}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1"><strong>Description:</strong> {card.description || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1"><strong>Risk Category:</strong> {template.riskCategory || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1"><strong>Likelihood:</strong> {template.likelihood || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1"><strong>Impact:</strong> {template.impact || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1"><strong>Risk Score:</strong> <Box component="span" sx={{ fontWeight: 'bold', color: riskScore >= 15 ? 'red' : riskScore >= 8 ? 'orange' : 'green' }}>{riskScore}</Box></Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1"><strong>Mitigation Plan:</strong> {template.mitigationPlan || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1"><strong>Contingency Plan:</strong> {template.contingencyPlan || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* <AssignmentIndIcon sx={{ color: '#1976d2' }} /> */}
              {/* <Tooltip title={assignee} arrow>
                <Avatar sx={{ bgcolor: '#1976d2', width: 24, height: 24, fontSize: '0.90rem' }}>{assignee.charAt(0).toUpperCase()}</Avatar>
              </Tooltip> */}
              <Typography variant="body1"><strong>Assignee:</strong> {assignee }</Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1"><strong>Status:</strong> {card.cardStatus || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1"><strong>Due Date:</strong> {card.dueDate ? new Date(card.dueDate).toLocaleDateString() : 'N/A'}</Typography>
          </Grid>
          {/* Attachments */}
          <Grid item xs={12}>
            <Typography variant="body1"><strong>Attachments:</strong></Typography>
            {template.attachments && template.attachments.length > 0 ? (
              <Box sx={{ pl: 2, mt: 1 }}>
                {template.attachments.map((file, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, borderBottom: '1px solid #eee' }}>
                    <Typography component="a" href={file.url} target="_blank" rel="noopener noreferrer" sx={{ color: '#4e54c8', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>{file.name}</Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ pl: 2, mt: 1 }}>No attachments</Typography>
            )}
          </Grid>
          {/* Comments */}
          <Grid item xs={12}>
            <Typography variant="body1"><strong>Comments:</strong></Typography>
            {template.comments && template.comments.length > 0 ? (
              <Box sx={{ mt: 1 }}>
                {template.comments.filter(comment => !comment.isDeleted).map((comment) => (
                  <Box key={comment.id} sx={{ display: 'flex', gap: 1 }}>
                    <Avatar sx={{ width: 24, height: 24, bgcolor: '#4e54c8' }}>{comment.authorEmail ? comment.authorEmail.charAt(0).toUpperCase() : '?'}</Avatar>
                    <Box>
                      <Typography variant="body2"><strong>{comment.authorEmail || 'Anonymous'}</strong></Typography>
                      <Typography variant="caption" color="text.secondary">{new Date(comment.timestamp).toLocaleString()}</Typography>
                      <Typography variant="body2">{comment.text}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">No comments</Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default RiskCardDetailsDialog;
